import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Breadcrumb.css";

const routeLabels = {
  humancases: "Humanitarian Cases",
  humancase: "Humanitarian Cases",
  projects: "Charity projects",
  beasponser: "Sponsorships",
  donations: "Donations",
  sukuk: "Sukuk",
  categories: "Categories",
  volunteer: "Volunteer",
  localAid: "Local Aid",
  sponsership: "Sponsorships",
  media: "Media",
  news: "News",
  auth: "Login",
  settings: "Settings",
  profile: "Profile",
  about: "About Us",
  "our-mission": "Our Mission",
  "contact-us": "Contact Us",
  "our-locations": "Our Locations",
  "our-partners": "Our Partners",
  mybag: "My Bag",
  favorites: "Favorites",
  checkout: "Checkout",
  "payment-method": "Payment Methods",
  "add-payment-method": "Add Payment Method",
  editprofile: "Edit Profile",
  sendgift: "Send Gift",
  "transaction-history": "Transaction History",
  renewals: "Late Payment Renewals",
  "events-attendance": "Events Attendance",
  "events-join": "Events Join",
  events_calendar: "Events Calendar",
  "grace-conservation": "Grace Conservation",
  grace_conversations: "Grace Conservation",
  guest: "Guest",
  "donate-idea": "DONATE YOUR IDEA",
  "donate-estate": "Donate Estate",
  "sponsor-projects": "Sponsored Projects",
  "sponsor-donations": "Sponsored Donations",
  "sponsor-shared-projects": "Shared Projects",
  landingpages: "Landing Pages",
  receipt: "Receipt",
  followups: "Follow-ups",
  request: "Request",
  project_share: "Project Shares",
};

const parentRoutes = {
  humancases: "categories",
  humancase: "categories",
  projects: "categories",
  beasponser: "categories",
  donations: "categories",
  sukuk: "categories",
  volunteer: "categories",
  localAid: "categories",
};

const hiddenRoots = new Set(["", "redirect", "projectshare", "donations"]);

const validPaths = new Set([
  "/",
  "/donations",
  "/humancases",
  "/humancase",
  "/projects",
  "/beasponser",
  "/sukuk",
  "/favorites",
  "/categories",
  "/checkout",
  "/mybag",
  "/profile",
  "/auth",
  "/auth/needToOTP",
  "/auth/reset-password",
  "/auth/get-account",
  "/auth/login-sponsor",
  "/volunteer",
  "/volunteer/areas-of-interest",
  "/volunteer/events",
  "/volunteer/event",
  "/volunteer/volunteer-details",
  "/volunteer/participants",
  "/volunteer/attachments",
  "/volunteer/training-courses",
  "/volunteer/languages",
  "/volunteer/E-services",
  "/volunteer/skills",
  "/volunteer/vln-registration",
  "/volunteer/link-account",
  "/localAid",
  "/localAid/setup-information",
  "/localAid/submit-request",
  "/localAid/requests",
  "/localAid/localAidProfile",
  "/localAid/relatives",
  "/localAid/link-account",
  "/localAid/local-aid-relation",
  "/settings",
  "/sponsership",
  "/sponsership/followups",
  "/sponsor-projects",
  "/donate-idea",
  "/donate-estate",
  "/sponsor-shared-projects",
  "/sponsor-donations",
  "/events-attendance",
  "/events-join",
  "/renewals",
  "/about",
  "/our-mission",
  "/contact-us",
  "/our-locations",
  "/media",
  "/news",
  "/grace_conversations/request",
  "/grace-conservation",
  "/payment-method",
  "/add-payment-method",
  "/editprofile",
  "/sendgift",
  "/guest",
  "/our-partners",
  "/redirect",
  "/transaction-history",
  "/events_calendar",
  "/projectshare",
  "/project_share",
  "/project_share/all",
  "/landingpages",
]);

const BreadcrumbContext = createContext({
  state: { route: null, pageName: null, hide: false },
  setBreadcrumb: () => {},
});

export const BreadcrumbProvider = ({ children }) => {
  const [state, setState] = useState({
    route: null,
    pageName: null,
    hide: false,
  });
  const setBreadcrumb = useCallback((next) => {
    setState((prev) => {
      if (
        prev.route === next.route &&
        prev.pageName === next.pageName &&
        prev.hide === next.hide
      ) {
        return prev;
      }
      return next;
    });
  }, []);
  const value = useMemo(() => ({ state, setBreadcrumb }), [state, setBreadcrumb]);
  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumb = () => useContext(BreadcrumbContext);

const Breadcrumb = ({ slot = "app" }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { state } = useBreadcrumb();
  const lng = JSON.parse(localStorage.getItem("language"))?.lang;

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const rootSegment = pathSegments[0] || "";

  if (pathSegments.length === 0 || hiddenRoots.has(rootSegment)) {
    return null;
  }

  const matchesCurrentRoute = state.route === location.pathname;
  const pageName = matchesCurrentRoute ? state.pageName : null;
  const hide = matchesCurrentRoute ? state.hide : false;

  if (hide) {
    return null;
  }

  const intermediateCrumbs = pathSegments.slice(0, -1).map((segment, index) => {
    const path = "/" + pathSegments.slice(0, index + 1).join("/");
    const label = routeLabels[segment] || decodeURIComponent(segment);
    return { path, label };
  });

  const parentSegment = parentRoutes[rootSegment];
  if (parentSegment) {
    intermediateCrumbs.unshift({
      path: "/" + parentSegment,
      label: routeLabels[parentSegment] || parentSegment,
    });
  }

  const lastSegment = pathSegments[pathSegments.length - 1];
  const lastSegmentLabel = lastSegment
    ? routeLabels[lastSegment] || decodeURIComponent(lastSegment)
    : "";
  const hasNamedLastSegment = lastSegment && routeLabels[lastSegment];

  let currentLabel;
  if (
    pageName &&
    hasNamedLastSegment &&
    pageName !== t(lastSegmentLabel) &&
    pageName !== lastSegmentLabel
  ) {
    intermediateCrumbs.push({
      path: location.pathname,
      label: lastSegmentLabel,
    });
    currentLabel = pageName;
  } else {
    currentLabel = pageName || lastSegmentLabel;
  }

  const lastIntermediate = intermediateCrumbs[intermediateCrumbs.length - 1];
  if (
    lastIntermediate &&
    currentLabel &&
    (lastIntermediate.label === currentLabel ||
      t(lastIntermediate.label) === currentLabel ||
      lastIntermediate.label === t(currentLabel))
  ) {
    intermediateCrumbs.pop();
  }

  const slotClass =
    slot === "mobile" ? "breadcrumb_slot_mobile" : "breadcrumb_slot_app";

  return (
    <nav
      className={`breadcrumb_desktop ${slotClass} ${
        lng === "arab" ? "breadcrumb_desktop_ar" : ""
      }`}
      aria-label="breadcrumb"
    >
      <Link to="/" className="breadcrumb_item">
        {t("Home")}
      </Link>
      {intermediateCrumbs.map((crumb) => (
        <React.Fragment key={crumb.path}>
          <span className="breadcrumb_separator">/</span>
          {validPaths.has(crumb.path) ? (
            <Link to={crumb.path} className="breadcrumb_item">
              {t(crumb.label)}
            </Link>
          ) : (
            <span className="breadcrumb_static">{t(crumb.label)}</span>
          )}
        </React.Fragment>
      ))}
      {currentLabel ? (
        <>
          <span className="breadcrumb_separator">/</span>
          <span className="breadcrumb_current">{t(currentLabel)}</span>
        </>
      ) : null}
    </nav>
  );
};

export default Breadcrumb;
